import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { optionalIntegerString, parseIntegerString } from "../units";
import { fetchJson } from "./client";
import { currentCache, immutableCache } from "./cacheClasses";
import { queryKeys } from "./queryKeys";
import { isValidBlockHeight } from "./validation";

// explorer-api serializes with JsonIgnoreCondition.WhenWritingNull, so nullable
// fields are absent (not `null`) on the wire. Model as optional here.

interface WireBlockHead {
  readonly height: string;
  readonly blockHash: string;
  readonly latestWinner?: WireBlockWinner;
}

interface WireBlockWinner {
  readonly height: string;
  readonly blockHash: string;
  readonly blockTime?: string;
  readonly txid: string;
  readonly txIndex: number;
  readonly miningDomainId: number;
  readonly recipientScriptPubKey: string;
  readonly sentenceText: string;
  readonly sentenceHex: string;
  readonly canonicalBlend: string;
  readonly rewardCogtoshi: string;
  readonly previousBlockHash?: string;
  readonly bip39WordIndices?: readonly number[];
}

export interface BlockHead {
  readonly height: bigint;
  readonly blockHash: string;
  // Most Bitcoin blocks carry no cogcoin submission, so the chain tip is
  // not necessarily a winning height. explorer-api inlines the latest winner
  // here so the UI does not need a follow-up call that would 404 on the
  // common "tip is not a winner" path.
  readonly latestWinner: BlockWinner | null;
}

export interface BlockWinner {
  readonly height: bigint;
  readonly blockHash: string;
  readonly blockTime: bigint | null;
  readonly txid: string;
  readonly txIndex: number;
  readonly miningDomainId: number;
  readonly recipientScriptPubKey: string;
  readonly sentenceText: string;
  readonly sentenceHex: string;
  readonly canonicalBlend: bigint;
  readonly rewardCogtoshi: bigint;
  readonly previousBlockHash: string | null;
  readonly bip39WordIndices: readonly number[] | null;
}

export function hydrateHead(wire: WireBlockHead): BlockHead {
  return {
    height: parseIntegerString(wire.height),
    blockHash: wire.blockHash,
    latestWinner: wire.latestWinner ? hydrateWinner(wire.latestWinner) : null,
  };
}

function hydrateWinner(wire: WireBlockWinner): BlockWinner {
  return {
    height: parseIntegerString(wire.height),
    blockHash: wire.blockHash,
    blockTime: optionalIntegerString(wire.blockTime),
    txid: wire.txid,
    txIndex: wire.txIndex,
    miningDomainId: wire.miningDomainId,
    recipientScriptPubKey: wire.recipientScriptPubKey,
    sentenceText: wire.sentenceText,
    sentenceHex: wire.sentenceHex,
    canonicalBlend: parseIntegerString(wire.canonicalBlend),
    rewardCogtoshi: parseIntegerString(wire.rewardCogtoshi),
    previousBlockHash: wire.previousBlockHash ?? null,
    bip39WordIndices: wire.bip39WordIndices ?? null,
  };
}

export function useBlockHead(): UseQueryResult<BlockHead> {
  return useQuery({
    queryKey: queryKeys.blocks.head(),
    queryFn: async () => hydrateHead(await fetchJson<WireBlockHead>("/api/v1/blocks/head")),
    ...currentCache,
  });
}

export function useBlockWinner(height: number | null): UseQueryResult<BlockWinner> {
  return useQuery({
    queryKey: queryKeys.blocks.winner(height ?? -1),
    queryFn: async () =>
      hydrateWinner(await fetchJson<WireBlockWinner>(`/api/v1/blocks/${height}/winner`)),
    enabled: isValidBlockHeight(height),
    ...immutableCache,
  });
}
